import Input from 'components/Common/Input';
import Switch from 'components/Common/Switch';
import Button from '@/components/Common/Button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateOrUpdateBusinessPlan, useGetBusinessPackage } from '@/apis/agency/businessPackages/hook';
import { Drawer, Divider, Typography, Box, IconButton } from '@mui/material';
import { useEffect } from 'react';
import { PackageResponse } from '@/apis/agency/businessPackages/types';
import PackagesFormSkeleton from './PackagesFormSkeleton';
import { IoClose } from 'react-icons/io5';
import { packagesFormSchema } from '@/utils/schema/validator/PackagesFormValidation';

export default function PackagesForm() {
  const navigate = useNavigate();
  const { packageId } = useParams<{ packageId: string }>();
  const { data: packageData, isLoading: loadingPlan } = useGetBusinessPackage();
  const { mutateAsync: updateBusinessPlan, isPending } = useCreateOrUpdateBusinessPlan();

  const { control, handleSubmit, reset, watch } = useForm<{ business_plan: PackageResponse }>({
    resolver: zodResolver(packagesFormSchema),
    defaultValues: {
      business_plan: {
        included_concurrency_per_location: 3
      } as PackageResponse
    }
  });

  const isPublic = watch('business_plan.public');

  useEffect(() => {
    if (packageData) {
      reset({ business_plan: packageData });
    }
  }, [packageData, reset]);

  const onSubmit = (data: { business_plan: PackageResponse }) => {
    updateBusinessPlan({ business_plan: data.business_plan });
  };

  const multilingual = watch('business_plan.price_details.multilingual_charge_per_min.enabled');
  const phoneNumberCharge = watch('business_plan.price_details.phone_number_charge.enabled');

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: 600,
          maxWidth: '100%',
          p: 0,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {loadingPlan ? (
        <PackagesFormSkeleton />
      ) : (
        <>
          {/* Sticky Header */}
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 999
            }}
          >
            <Box>
              <Typography variant='h6'>{packageId ? 'Edit Package' : 'Add Package'}</Typography>
              <Typography variant='body2' color='text.secondary'>
                Enter details of your pricing package.
              </Typography>
            </Box>
            <IconButton onClick={() => navigate(-1)} size='small'>
              <IoClose size={24} />
            </IconButton>
          </Box>

          <Box
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}
          >
            <Box sx={{ flex: 1, overflow: 'auto', p: 3, pb: 20 }}>
              <Box display='flex' alignItems='center' gap={2} mb={2}>
                <Input name='business_plan.name' control={control} label='Package Name' required fullWidth />
                <Box display='flex' flexDirection='row' alignItems='center'>
                  <Typography>{isPublic ? 'Public' : 'Private'}</Typography>
                  <Switch name='business_plan.public' key={String(isPublic)} control={control} />
                </Box>
              </Box>

              <Box display='flex' alignItems='center' gap={2} mb={2}>
                <Input
                  name='business_plan.base_price'
                  type='number'
                  control={control}
                  label='Base Price'
                  required
                  fullWidth
                />
              </Box>

              <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} mb={2}>
                <Input
                  name='business_plan.available_minutes'
                  type='number'
                  control={control}
                  label='Included Call Minutes(Base Price)'
                  required
                />
                <Input
                  name='business_plan.additional_per_min_charge'
                  type='number'
                  control={control}
                  label='Cost per Additional Minute'
                  required
                  fullWidth
                />
              </Box>

              <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} mb={2}>
                <Input
                  name='business_plan.available_warm_call_minutes'
                  type='number'
                  control={control}
                  label='Included Warm Call Minutes(Base Price)'
                  required
                />
                <Input
                  name='business_plan.additional_warm_call_charge'
                  type='number'
                  control={control}
                  label='Cost per Additional Warm Call Minute'
                  required
                  fullWidth
                />
              </Box>

              <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} mb={2}>
                <Input
                  name='business_plan.available_sms_segments'
                  type='number'
                  control={control}
                  label='Included SMS Segments'
                  required
                  placeholder='Enter no. of segments'
                />
                <Input
                  name='business_plan.additional_per_sms_segment_charge'
                  type='number'
                  control={control}
                  label='Cost per Additional SMS Segment'
                  required
                  fullWidth
                />
              </Box>

              <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} mb={2}>
                <Input
                  name='business_plan.included_ai_tokens'
                  type='number'
                  control={control}
                  label='Included AI Tokens / Month'
                  required
                  placeholder='Enter No of AI Tokens'
                />
                <Input
                  name='business_plan.additional_per_1k_ai_tokens_charge'
                  type='number'
                  control={control}
                  label='Cost per 1K Additional Tokens'
                  required
                  placeholder='Enter Price'
                  fullWidth
                />
              </Box>

              <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} mb={2}>
                <Input
                  name='business_plan.available_chats'
                  type='number'
                  control={control}
                  label='Included AI Chat Messages(Base Price)'
                  required
                />
                <Input
                  name='business_plan.additional_per_chat_charge'
                  type='number'
                  control={control}
                  label='Cost per Additional AI Chat Message'
                  required
                  fullWidth
                />
              </Box>

              <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} mb={2}>
                <Input
                  name='business_plan.included_locations'
                  type='number'
                  control={control}
                  label='Included Location'
                  required
                />
                <Input
                  name='business_plan.additional_per_location_charge'
                  type='number'
                  control={control}
                  label='Cost Per Additional Location'
                  required
                  fullWidth
                />
              </Box>

              <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} mb={2}>
                <Input
                  name='business_plan.included_storage'
                  type='number'
                  control={control}
                  label='Included Storage (GB)'
                  required
                />
                <Input
                  name='business_plan.additional_per_storage_charge'
                  type='number'
                  control={control}
                  label='Cost Per Additional GB'
                  required
                  fullWidth
                />
              </Box>

              <Box display='grid' gridTemplateColumns='1fr' gap={2} mb={2}>
                <Input
                  name='business_plan.included_concurrency_per_location'
                  type='number'
                  control={control}
                  label='Included Concurrency Per Location'
                  required
                  fullWidth
                  placeholder='Enter Concurrency No. Here'
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Sections */}
              <Section
                title='Multilingual Charges'
                description='Enable additional charges for multilingual support'
                toggleName='business_plan.price_details.multilingual_charge_per_min.enabled'
                control={control}
              >
                {multilingual && (
                  <Input
                    name='business_plan.price_details.multilingual_charge_per_min.unit_amount'
                    type='number'
                    control={control}
                    label='Price Per Minute'
                    fullWidth
                    required
                  />
                )}
              </Section>

              <Section
                title='Phone Number Charges'
                description='Enable recurring charges for phone numbers. Phone number charges vary by country and are based on the number destination and local carrier rates.'
                toggleName='business_plan.price_details.phone_number_charge.enabled'
                control={control}
              >
                {phoneNumberCharge && (
                  <Input
                    name='business_plan.price_details.phone_number_charge.unit_amount'
                    type='number'
                    control={control}
                    label='Surcharge Amount'
                    fullWidth
                    required
                  />
                )}
              </Section>

              <Section
                title='Integration Charges'
                description='Enable integration charges based on connections'
                toggleName='business_plan.price_details.integration_charge.enabled'
                control={control}
              />
            </Box>

            <Box
              display='flex'
              justifyContent='flex-end'
              sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                position: 'sticky',
                bottom: 0,
                zIndex: 1
              }}
            >
              <Button onClick={() => navigate(-1)} variant='outlined' sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button type='submit' isPending={isPending} variant='contained'>
                {packageId ? 'Update' : 'Add'}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Drawer>
  );
}

function Section({ title, description, toggleName, control, children }: any) {
  return (
    <Box mb={3}>
      <Box display='flex' justifyContent='space-between' alignItems='start' mb={2}>
        <Box>
          <Typography fontWeight={500}>{title}</Typography>
          <Typography variant='body2' color='text.secondary'>
            {description}
          </Typography>
        </Box>
        <Switch name={toggleName} control={control} />
      </Box>
      {children}
    </Box>
  );
}
